import { useEffect, useCallback, useState, useRef } from 'react'
import DOMPurify from 'dompurify'
import { useAuth } from '@praxis/component-auth'
import { Button, Input } from '@design-systems/canvas-ui'
import { useEnv } from '@praxis/component-runtime-env'
import Icon, {
  CloseIcon,
  MaximizeIcon,
  PaperPlaneIcon,
  AppMydayIcon,
  MinimizeIcon,
  HuddleIcon,
} from '@design-systems/react-icons'
import {
  ChatBotContainer,
  StyledButton,
  StyledHeader,
  StyledChatBotWindow,
  StyledToastMessage,
  StyledOptions,
} from './styles'
import LostConnectionPopUp from './LostConnectionPopUp'
import { getLocation } from '@praxis/runtime-utils/getLocation'

const BUSINESS_ID = 'BID_0027_MB'

export const Chatbot = (props) => {
  const { session, isAuthenticated } = useAuth()
  const { userInfo } = session ?? {}
  const wsRef = useRef(null)
  const chatBotWindowRef = useRef(null)
  const [isConnected, setIsConnected] = useState(false)
  const [waitingToReconnect, setWaitingToReconnect] = useState(false)
  const [textMessage, setTextMessage] = useState('')
  const [messageList, setMessageList] = useState([])
  const [shouldExpand, setShouldExpand] = useState(false)
  const [isInputDisabled, setIsInputDisabled] = useState(false)
  const [retryInProgress, setRetryInProgress] = useState(false)
  const [enableReload, setEnableReload] = useState(false)
  const env = useEnv()
  const chatbotURL = `${env.mfelcm.chatWidgetUrl}${env.apiKey}`

  // These regex case-insensitive. so it will match all the formats.
  const mainMenuRegex = /Main Menu/i
  const goBackRegex = /Go Back/i
  const yesRegex = /Yes/i
  const noRegex = /No/i

  const getFormattedMessage = (message) => {
    const regex = /\[(.*?)\]\((.*?)\)/g
    return message?.replace(regex, '<a href="$2" target="_blank">$1</a>') ?? ''
  }

  const scrollToBottom = (ele, ms) => {
    setTimeout(() => {
      ele.scrollTo({ left: 0, top: ele.scrollHeight, behavior: 'smooth' })
    }, ms)
  }

  const reorderOptions = (menuItems) => {
    const removeMainMenuAndGoBack = menuItems.filter(
      (item) =>
        !mainMenuRegex.test(item.title) && !goBackRegex.test(item.title),
    )

    const reorderedOptions = [
      ...removeMainMenuAndGoBack,
      menuItems.find((item) => goBackRegex.test(item.title)),
      menuItems.find((item) => mainMenuRegex.test(item.title)),
    ].filter(Boolean)
    return reorderedOptions
  }

  const sanitizeInputText = (message) => {
    return DOMPurify.sanitize(message)
  }

  const getCurrentUserFullName = useCallback(
    () => `${userInfo?.firstName}.${userInfo?.lastName}`,
    [userInfo?.firstName, userInfo?.lastName],
  )

  const getWSRequestObject = useCallback(
    (action, message) => {
      const currentUser = getCurrentUserFullName()

      return {
        action,
        message,
        author: currentUser,
        alias: currentUser,
        email: session?.userInfo?.email,
        business_id: BUSINESS_ID,
        user_id: currentUser,
        client_info: { id: 'target.com' },
        user_type: 'guest',
        client_type: 'widget',
      }
    },
    [getCurrentUserFullName, session?.userInfo?.email],
  )

  const retryConnection = useCallback(() => {
    setRetryInProgress(true)
    if (isAuthenticated()) {
      wsRef.current = new WebSocket(chatbotURL)

      wsRef.current.onopen = () => {
        setIsConnected(true)
        const requestObject = getWSRequestObject('message', '__ping__')
        wsRef.current?.send(JSON.stringify(requestObject))
        setRetryInProgress(false)
      }

      wsRef.current.onclose = () => {
        setIsConnected(false)
        setWaitingToReconnect(true)
        setRetryInProgress(false)
        setEnableReload(true)
      }
    } else {
      const location = getLocation()
      location.reload()
    }
  }, [getWSRequestObject, isAuthenticated, chatbotURL])

  useEffect(() => {
    chatBotWindowRef?.current && scrollToBottom(chatBotWindowRef.current, 0)
    if (messageList.at(-1)?.options?.length) {
      setIsInputDisabled(true)
    } else {
      setIsInputDisabled(false)
    }
  }, [messageList])

  useEffect(() => {
    if (isAuthenticated() && session) {
      if (waitingToReconnect) {
        return
      }

      if (!wsRef.current) {
        wsRef.current = new WebSocket(chatbotURL)
      }

      wsRef.current.onopen = () => {
        setIsConnected(true)
        const requestObject = getWSRequestObject('message', '__ping__')

        wsRef.current?.send(JSON.stringify(requestObject))
      }

      wsRef.current.onclose = () => {
        setIsConnected(false)
        setWaitingToReconnect(true)
      }
    }

    return () => {
      wsRef.current?.close()
      wsRef.current = undefined
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waitingToReconnect, session, chatbotURL])

  const handleTextChange = (event) => {
    setTextMessage(event.target.value)
  }

  const sendMessage = (message) => {
    if (isAuthenticated()) {
      const currentUser = getCurrentUserFullName()
      const requestObject = getWSRequestObject(
        'message',
        sanitizeInputText(message),
      )

      wsRef.current?.send(JSON.stringify(requestObject))

      if (wsRef.current !== undefined) {
        wsRef.current.onmessage = (e) => {
          const { message, author, time, options } = JSON.parse(e.data) ?? {}
          setMessageList((prevState) => {
            return [
              ...prevState,
              {
                time,
                ...(options && { options: reorderOptions(options) }),
                message: getFormattedMessage(message),
                type: author === currentUser ? 'user' : 'agent',
              },
            ]
          })
          setTextMessage('')
        }
      }
    } else {
      const location = getLocation()
      location.reload()
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && textMessage) {
      sendMessage(textMessage, 'search_bar')
    }
  }

  const handleButtonClick = (message) => {
    sendMessage(message)
  }

  const handleExpandButtonClick = () => {
    setShouldExpand((prevState) => !prevState)
  }

  return (
    <ChatBotContainer
      expand={shouldExpand}
      className="chatbot-window hc8-mr-8x"
    >
      <StyledHeader className="chatbot-window-header display-flex hc8-clr-inverse hc8-pt-4x hc8-pb-4x">
        <div className="hc8-ml-4x">
          <Icon size="lg" svg={CloseIcon} />
          <strong>
            <span className="hc8-ml-2x">MVS Bot</span>
          </strong>{' '}
          <span className="hc8-fs-150 hc8-clr-inverse hc8-ml-4x"></span>
        </div>
        <div className="display-flex">
          <StyledButton
            aria-label={`${
              shouldExpand ? 'maximize' : 'minimize'
            } chatbot window`}
            isIconOnly
            variant="ghost"
            onClick={handleExpandButtonClick}
          >
            <Icon svg={shouldExpand ? MinimizeIcon : MaximizeIcon} />
          </StyledButton>
          <div className="hc8-mr-4x">
            <StyledButton
              aria-label="close chatbot window"
              isIconOnly
              variant="ghost"
              onClick={() => props.setIsOpen(false)}
            >
              <Icon svg={CloseIcon} />
            </StyledButton>
          </div>
        </div>
      </StyledHeader>
      <div
        className="display-flex chatbot-window-content-container"
        style={{ flexDirection: 'column' }}
      >
        <StyledChatBotWindow
          diableScroll={!!(!isConnected && waitingToReconnect)}
          ref={chatBotWindowRef}
          className="chatbot-window-content"
        >
          <StyledToastMessage>
            <div>
              Welcome to Quick Help! I'm here for any questions about MVS, say
              Hi to get started.
            </div>
          </StyledToastMessage>
          {messageList.map((list, index) =>
            list.type === 'agent' ? (
              <div key={index} className="display-flex hc8-mt-4x hc8-mb-4x">
                <Icon
                  size="lg"
                  svg={HuddleIcon}
                  className="hc8-mt-3x hc8-ml-4x hc8-mr-1x"
                />
                <div className="messages-width hc8-mt-1x">
                  <div className="received-message">
                    <div className="hc8-pa-3x box arrow-left hc8-fs-150">
                      <span
                        dangerouslySetInnerHTML={{ __html: list.message }}
                      />
                    </div>
                  </div>
                  {index === messageList.length - 1 && (
                    <div className="hc8-ml-4x">
                      {list?.options?.map((option) => (
                        <StyledOptions
                          key={option.title}
                          isInlineBlock={
                            mainMenuRegex.test(option.title) ||
                            goBackRegex.test(option.title) ||
                            yesRegex.test(option.title) ||
                            noRegex.test(option.title)
                          }
                          isNavigationButton={
                            mainMenuRegex.test(option.title) ||
                            goBackRegex.test(option.title)
                          }
                        >
                          <Button
                            className="hc8-mt-3x hc8-mr-2x"
                            onClick={() =>
                              handleButtonClick(option.title, 'options')
                            }
                          >
                            {option.title}
                          </Button>
                        </StyledOptions>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div
                key={index}
                className="display-flex hc8-mt-4x hc8-mb-4x hc8-mr-2x row-reverse"
              >
                <Icon className="profile-icon" size="md" svg={AppMydayIcon} />
                <div className="messages-width">
                  <div className="sent-message">
                    <div className="hc8-pa-3x box-sent arrow-right hc8-fs-150">
                      <span
                        dangerouslySetInnerHTML={{ __html: list.message }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ),
          )}
        </StyledChatBotWindow>
        <div className="chatbot-input-container display-flex hc8-pb-6x hc8-pt-4x hc8-pl-4x hc8-pr-3x">
          <div>
            <Input.Text
              onChange={handleTextChange}
              placeholder={
                isInputDisabled
                  ? 'Please select from the menu options'
                  : 'Please enter your question'
              }
              value={textMessage}
              onKeyPress={handleKeyPress}
              data-testid="message-input"
              isDisabled={isInputDisabled}
            />
          </div>
          <div className="hc8-mr-2x hc8-ml-1x">
            <Button
              aria-label="send chatbot message"
              isIconOnly
              data-testid="send-chatbot-message"
              isDisabled={!textMessage}
              onClick={() => handleButtonClick(textMessage, 'search_bar')}
              variant="ghost"
            >
              <Icon svg={PaperPlaneIcon} />
            </Button>
          </div>
        </div>
      </div>
      {!isConnected && waitingToReconnect && (
        <LostConnectionPopUp
          shouldExpand={shouldExpand}
          retryConnection={retryConnection}
          retryInProgress={retryInProgress}
          enableReload={true}
        />
      )}
    </ChatBotContainer>
  )
}

export default Chatbot
